import React, { useState, FormEvent, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { logger } from '../../utils/logger';
import { useAnalytics } from '../../hooks/useAnalytics';
import PageHeader from '../common/PageHeader';

/**
 * PasswordReset component for requesting password reset emails
 */
const PasswordReset: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  
  const { resetPassword } = useAuth();
  // Add analytics hooks
  const { trackPageView, trackCustomEvent } = useAnalytics();
  
  // Track page view when component mounts
  useEffect(() => {
    trackPageView({
      page_title: 'Password Reset',
      page_location: window.location.href
    });
  }, [trackPageView]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    try {
      setError('');
      setMessage('');
      setLoading(true);
      
      // Track password reset attempt
      trackCustomEvent('password_reset_attempt', {
        email_domain: email.split('@')[1]
      });
      
      await resetPassword(email);
      
      // Track successful password reset request
      trackCustomEvent('password_reset_email_sent', {
        email_domain: email.split('@')[1]
      });
      
      setMessage('Check your inbox for password reset instructions');
      logger.info('📧 Password reset email sent');
    } catch (err: any) {
      logger.error('❌ Password reset error', err);
      
      // Track error
      trackCustomEvent('password_reset_error', {
        error_code: err.code || 'unknown',
        error_message: err.message || 'Unknown error'
      });
      
      if (err.code === 'auth/user-not-found') {
        // Don't reveal if user exists for security reasons
        setMessage('Check your inbox for password reset instructions');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email format');
      } else {
        setError('Failed to send reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Reset Password"
        subtitle="Request a password reset link"
      />
      <div className="password-reset-container">
        <h2>Reset Your Password</h2>
        
        {error && <div className="auth-error">{error}</div>}
        {message && <div className="auth-success">{message}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              required
              placeholder="Enter your email address"
              autoComplete="email"
            />
          </div>
          
          <button 
            type="submit" 
            className="primary-button"
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>
        
        <div className="auth-links">
          <Link to="/sign-in">Back to Sign In</Link>
        </div>
      </div>
    </>
  );
};

export default PasswordReset;
